import { useState } from 'react';

interface ImageGalleryProps {
  images: string[];
  productName: string;
}

export default function ImageGallery({ images, productName }: ImageGalleryProps) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [zoomed, setZoomed] = useState(false);

  const goNext = () => setActiveIdx((i) => (i + 1) % images.length);
  const goPrev = () => setActiveIdx((i) => (i - 1 + images.length) % images.length);

  return (
    <div className="flex flex-col gap-4">
      {/* Main Image */}
      <div className="relative bg-[#F5F3F0] rounded-2xl overflow-hidden h-[460px] md:h-[520px] group">
        <img
          src={images[activeIdx]}
          alt={`${productName} - view ${activeIdx + 1}`}
          className={`w-full h-full object-cover object-top transition-all duration-500 cursor-zoom-in ${zoomed ? 'scale-150' : 'scale-100'}`}
          onClick={() => setZoomed(!zoomed)}
        />

        {/* Navigation arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-full cursor-pointer hover:bg-white transition-colors opacity-0 group-hover:opacity-100"
              aria-label="Previous image"
            >
              <i className="ri-arrow-left-s-line text-[#1A1A1A] text-lg"></i>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); goNext(); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-full cursor-pointer hover:bg-white transition-colors opacity-0 group-hover:opacity-100"
              aria-label="Next image"
            >
              <i className="ri-arrow-right-s-line text-[#1A1A1A] text-lg"></i>
            </button>
          </>
        )}

        {/* Image count badge */}
        <div className="absolute top-4 right-4 bg-black/40 backdrop-blur-sm text-white font-outfit text-xs px-3 py-1.5 rounded-full">
          {activeIdx + 1} / {images.length}
        </div>

        {/* Zoom hint */}
        <div className="absolute bottom-4 left-4 bg-black/40 backdrop-blur-sm text-white/70 font-inter text-xs px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
          <i className="ri-zoom-in-line mr-1"></i>Click to zoom
        </div>
      </div>

      {/* Thumbnail Strip */}
      {images.length > 1 && (
        <div className="flex gap-3 overflow-x-auto pb-1">
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={() => setActiveIdx(idx)}
              className={`flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden cursor-pointer transition-all duration-200 ${
                activeIdx === idx
                  ? 'ring-2 ring-[#C4FF61] ring-offset-2 opacity-100'
                  : 'opacity-60 hover:opacity-80'
              }`}
              aria-label={`View image ${idx + 1}`}
            >
              <img
                src={img}
                alt={`${productName} thumbnail ${idx + 1}`}
                className="w-full h-full object-cover object-top"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
